import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, CircleCheck, MoreHorizontal, Package, PencilLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ProductProductReadModel } from '@r/api/generated/client.schemas.ts';
import { isStockUnlimited } from '@r/utils/product-stock.ts';
import { cn } from '@r/utils/utils.ts';
import { Button } from '@ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { ProductPublicationStatusBadge } from './product-publication-status-badge.tsx';
import { ProductStockStatusIcon } from './product-stock-status-icon.tsx';

interface UseCustomShopProductColumnsOptions {
  onEdit: (product: ProductProductReadModel) => void;
  onArchive: (product: ProductProductReadModel) => void;
  onReactivate: (product: ProductProductReadModel) => void;
  canManageProducts: boolean;
}

/** Construit les colonnes du catalogue en appliquant la locale aux prix et aux dates. */
export function useCustomShopProductColumns({
  onEdit,
  onArchive,
  onReactivate,
  canManageProducts
}: UseCustomShopProductColumnsOptions): ColumnDef<ProductProductReadModel>[] {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }),
    [locale]
  );
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'short' }),
    [locale]
  );

  return useMemo(
    () => [
      {
        id: 'stockStatus',
        header: () => (
          <span
            className="flex items-center justify-center text-muted-foreground"
            title={t('admin.shops.products.column.stock.status')}
          >
            <Package className="size-4" aria-hidden />
            <span className="sr-only">{t('admin.shops.products.column.stock.status')}</span>
          </span>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const product = row.original;

          return (
            <ProductStockStatusIcon
              stock={product.stock}
              lowStockAlert={product.lowStockAlert}
              isArchived={product.isArchived}
            />
          );
        }
      },
      {
        accessorKey: 'id',
        header: t('admin.shops.products.column.id')
      },
      {
        id: 'image',
        header: t('admin.shops.products.column.image'),
        cell: ({ row }) => {
          const product = row.original;
          const image = product.images?.[0]?.pathUri;

          return image ? (
            <div className="relative size-10">
              <img
                src={image}
                alt=""
                className={cn(
                  'size-10 rounded-md border object-cover',
                  product.isArchived && 'opacity-50 grayscale'
                )}
                loading="lazy"
              />
              {product.isArchived && (
                <span
                  className="pointer-events-none absolute inset-0 flex items-center justify-center"
                  aria-hidden
                >
                  <span className="block h-px w-full rotate-45 rounded-full bg-muted-foreground/80" />
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        }
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('admin.shops.products.column.name')} />
        ),
        cell: ({ row }) => (
          <span className={cn(row.original.isArchived && 'text-muted-foreground')}>
            {row.original.name}
          </span>
        )
      },
      {
        id: 'publicationStatus',
        header: t('admin.shops.products.column.publication.status'),
        enableSorting: false,
        cell: ({ row }) => <ProductPublicationStatusBadge product={row.original} />
      },
      {
        accessorKey: 'description',
        header: t('admin.shops.products.column.description'),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="block max-w-64 truncate" title={row.original.description ?? undefined}>
            {row.original.description || '—'}
          </span>
        )
      },
      {
        accessorKey: 'designationCom',
        header: t('admin.shops.products.column.designation.com'),
        enableSorting: false,
        cell: ({ row }) => row.original.designationCom || '—'
      },
      {
        accessorKey: 'ean',
        header: t('admin.shops.products.column.ean'),
        enableSorting: false,
        cell: ({ row }) => row.original.ean || '—'
      },
      {
        id: 'brand',
        accessorFn: (product) => product.brand?.name ?? '',
        header: t('admin.shops.products.column.brand'),
        enableSorting: false,
        cell: ({ row }) => row.original.brand?.name || '—'
      },
      {
        id: 'price',
        accessorFn: (product) => product.price,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('admin.shops.products.column.price')} />
        ),
        cell: ({ row }) => {
          if (row.original.dynamicPrice) return t('item.dynamic.price');
          if (row.original.price == null) return '—';

          return currencyFormatter.format(row.original.price);
        }
      },
      {
        accessorKey: 'pointUnitPrice',
        header: t('admin.shops.products.column.point.unit.price'),
        enableSorting: false
      },
      {
        accessorKey: 'tva',
        header: t('admin.shops.products.column.tva'),
        enableSorting: false,
        cell: ({ row }) => `${row.original.tva} %`
      },
      {
        accessorKey: 'shippingPrice',
        header: t('admin.shops.products.column.shipping.price'),
        enableSorting: false,
        cell: ({ row }) => currencyFormatter.format(row.original.shippingPrice)
      },
      {
        id: 'stock',
        header: t('admin.shops.products.column.stock'),
        cell: ({ row }) => {
          const { stock } = row.original;

          if (isStockUnlimited(stock)) {
            return t('admin.shops.products.stock.unlimited');
          }

          return stock;
        }
      },
      {
        accessorKey: 'lowStockAlert',
        header: t('admin.shops.products.column.low.stock.alert'),
        enableSorting: false,
        cell: ({ row }) => row.original.lowStockAlert ?? '—'
      },
      {
        id: 'isDemat',
        accessorFn: (product) => product.isDemat,
        header: t('admin.shops.products.column.code'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.isDemat
            ? t('admin.shops.products.column.code.demat')
            : t('admin.shops.products.column.code.none')
      },
      {
        id: 'codeDetail',
        accessorFn: (product) =>
          product.publicMetadatas?.find((metadata) => metadata.metaKey === 'codeDetail')
            ?.metaValue ?? '',
        header: t('admin.shops.products.column.code.detail'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.publicMetadatas?.find((metadata) => metadata.metaKey === 'codeDetail')
            ?.metaValue || '—'
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('admin.shops.products.column.created.at')}
          />
        ),
        cell: ({ row }) => dateFormatter.format(new Date(row.original.createdAt))
      },
      {
        id: 'search',
        accessorFn: (product) => product.name,
        enableSorting: false,
        enableHiding: false
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const product = row.original;

          if (!canManageProducts) {
            return null;
          }

          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 data-[state=open]:bg-muted"
                    aria-label={t('admin.shops.products.action.menu.aria', {
                      productName: product.name
                    })}
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      onEdit(product);
                    }}
                  >
                    <PencilLine className="mr-2 size-4" />
                    {t('admin.shops.products.action.edit')}
                  </DropdownMenuItem>
                  {!product.isArchived && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          onArchive(product);
                        }}
                      >
                        <Archive className="mr-2 size-4" />
                        {t('admin.shops.products.action.archive')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {product.isArchived && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          onReactivate(product);
                        }}
                      >
                        <CircleCheck className="mr-2 size-4" />
                        {t('admin.shops.products.action.reactivate')}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        }
      }
    ],
    [canManageProducts, currencyFormatter, dateFormatter, onArchive, onEdit, onReactivate, t]
  );
}
